import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { API } from "../api";
import { Loader2, Download, CheckCircle2, Gift, CalendarClock, Smartphone, Share2, Send } from "lucide-react";
import { RedemptionHistory } from "../components/RedemptionHistory";
import { VideoMessageReveal } from "../components/VideoMessageReveal";
import { ThankYouReaction } from "../components/ThankYouReaction";

const statusMap = {
  active: { label: "Active", color: "#16a34a", bg: "#dcfce7" },
  part_used: { label: "Partially used", color: "#d97706", bg: "#fef3c7" },
  used: { label: "Fully redeemed", color: "#dc2626", bg: "#fee2e2" },
};
export default function VoucherPage() {
  const { code } = useParams();
  const [sp] = useSearchParams();
  const isNew = sp.get("new") === "1";
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showTip, setShowTip] = useState(false);

  const saveToPhone = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: "My gift voucher", text: "My gift voucher", url }); return; } catch { /* cancelled */ }
    }
    setShowTip((s) => !s);
  };

  useEffect(() => {
    api.get(`/voucher/${code}`)
      .then(({ data }) => setV(data))
      .catch(() => setError("Voucher not found"))
      .finally(() => setLoading(false));
  }, [code]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={40} /></div>;
  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-[#5a6b80] text-lg">{error}</p>
      <Link to="/check-balance" className="gp-btn-teal rounded-full px-6 py-2.5 font-medium">Check another code</Link>
    </div>
  );

  const st = statusMap[v.status] || statusMap.active;
  const brand = v.brand_color || "#16243b";
  const pct = v.original_amount > 0 ? (v.balance / v.original_amount) * 100 : 0;
  const expiredActive = v.expired && v.status !== "used";
  const badge = expiredActive ? { label: "Expired", color: "#dc2626", bg: "#fee2e2" } : st;

  return (
    <div className="min-h-screen bg-[#f6f8fb] py-12 px-4">
      <div className="max-w-[480px] mx-auto">
        {isNew && (
          <div className="bg-[#dcfce7] text-[#166534] rounded-xl p-4 mb-6 flex items-center gap-2">
            <CheckCircle2 size={20} /> <span className="font-medium">Payment successful! Your voucher is ready.</span>
          </div>
        )}
        {v.scheduled_send_at && new Date(v.scheduled_send_at) > new Date() && (
          <div className="bg-[#eef7ff] text-[#2a4a6b] rounded-xl p-4 mb-6 flex items-center gap-2" data-testid="voucher-scheduled-notice">
            <Send size={18} /> <span className="font-medium">Scheduled — this voucher will be emailed to {v.recipient_name || "the recipient"} on {new Date(v.scheduled_send_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}.</span>
          </div>
        )}
        {v.video_url && <VideoMessageReveal code={v.code} url={v.video_url} from={v.buyer_name} brand={brand} autoOpen={sp.get("video") === "1"} />}
        {!isNew && v.reactions_enabled && !v.reaction_sent && v.buyer_name && <ThankYouReaction code={v.code} buyerName={v.buyer_name} businessName={v.business_name} />}
        <div className="bg-white rounded-3xl overflow-hidden shadow-[0_20px_50px_rgba(20,30,55,0.12)]">
          <div className="p-8 text-white" style={{ background: brand }}>
            <div className="flex items-center gap-2 opacity-90"><Gift size={20} /><span className="text-sm">Gift Voucher</span></div>
            <h1 className="text-2xl font-bold mt-3">{v.business_name}</h1>
            <p className="opacity-85 mt-1">{v.product_title}</p>
          </div>
          <div className="p-8">
            {v.kind === "experience" ? (<>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#5a6b80]">Experience voucher</span>
                <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ color: badge.color, background: badge.bg }}>{badge.label}</span>
              </div>
              <p className="text-[2rem] font-bold text-[#16243b] leading-tight" data-testid="voucher-experience-status">{v.balance > 0 ? "Ready to enjoy" : "Already enjoyed"}</p>
              <p className="text-sm text-[#8a97a8] mt-1">1 × {v.product_title}</p>
            </>) : (<>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#5a6b80]">Remaining balance</span>
              <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ color: badge.color, background: badge.bg }}>{badge.label}</span>
            </div>
            <p className="text-[3rem] font-bold text-[#16243b] leading-none">£{v.balance.toFixed(2)}</p>
            <p className="text-sm text-[#8a97a8] mt-1">of £{v.original_amount.toFixed(2)} original value</p>
            <div className="h-2 bg-gray-100 rounded-full mt-4 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: "#2ec9c9" }} />
            </div>
            </>)}

            {v.expires_at && (
              <p className={`text-sm mt-4 flex items-center gap-1.5 ${expiredActive ? "text-red-600 font-medium" : "text-[#8a97a8]"}`}>
                <CalendarClock size={15} /> {expiredActive ? "Expired on" : "Valid until"} {v.expires_at.slice(0, 10)}
              </p>
            )}
            {expiredActive && (
              <div className="mt-3 bg-amber-50 border border-amber-100 text-amber-700 rounded-xl p-3 text-sm">
                This voucher has expired. The business may still choose to honour it at their discretion.
              </div>
            )}

            <RedemptionHistory redemptions={v.redemptions} experience={v.kind === "experience"} />

            <div className="mt-6 bg-[#f6f8fb] rounded-xl p-4 flex items-center gap-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&margin=0&data=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : v.code)}`}
                alt="Voucher QR code" width="88" height="88" className="rounded-lg bg-white p-1 shrink-0" data-testid="voucher-qr" />
              <div>
                <p className="text-xs text-[#8a97a8] mb-1">Voucher code</p>
                <p className="font-mono font-bold text-lg tracking-wider text-[#16243b]">{v.code}</p>
                <p className="text-xs text-[#8a97a8] mt-1">Show this QR in-store to redeem.</p>
              </div>
            </div>

            <button onClick={saveToPhone} data-testid="save-to-phone-btn"
              className="w-full rounded-xl py-3 font-medium mt-4 flex items-center justify-center gap-2 border-2 border-[#16243b] text-[#16243b] hover:bg-[#16243b] hover:text-white transition-colors">
              {navigator.share ? <Share2 size={18} /> : <Smartphone size={18} />} Save to phone
            </button>
            {showTip && (
              <div className="mt-3 bg-[#eef7ff] border border-[#d6e8fb] text-[#2a4a6b] rounded-xl p-3 text-sm" data-testid="save-to-phone-tip">
                <p className="font-medium mb-1">Keep this voucher on your phone:</p>
                <p><strong>iPhone:</strong> tap the Share icon in Safari → <em>Add to Home Screen</em>.</p>
                <p><strong>Android:</strong> tap the ⋮ menu in Chrome → <em>Add to Home screen</em>.</p>
              </div>
            )}

            <a href={`${API}/voucher/${v.code}/pdf`} target="_blank" rel="noreferrer"
              className="gp-btn-teal w-full rounded-xl py-3 font-medium mt-3 flex items-center justify-center gap-2">
              <Download size={18} /> Download PDF voucher
            </a>
            {v.terms && <p className="text-xs text-[#8a97a8] mt-5 leading-relaxed"><strong>Terms:</strong> {v.terms}</p>}
          </div>
        </div>
        <p className="text-center text-[#8a97a8] text-sm mt-6">Powered by GiftVouchers.app</p>
      </div>
    </div>
  );
}
